<template>
  <el-dialog
    v-model="dialogVisible"
    :title="$t('ucAccount.memberEdit.title')"
    width="min(900px, calc(100vw - 16px))"
    class="association-member-info-edit-dialog"
    destroy-on-close
    :close-on-click-modal="false"
    top="1vh"
    @closed="handleClosed"
  >
    <div class="identity-form-content">
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="135px"
        class="identity-form"
      >
        <el-form-item
          prop="unitAvatar"
          :label="$t('ucAccount.memberEdit.form.unitAvatar')"
          required
          class="upload-form-item"
        >
          <div class="upload-wrapper">
            <ImageUpload
              v-model="form.unitAvatar"
              :width="384"
              :height="216"
              :alt="$t('ucAccount.memberEdit.upload.alt')"
              :label="$t('ucAccount.memberEdit.upload.label')"
              :max-size="10"
            />
          </div>
        </el-form-item>

        <el-form-item
          :label="$t('ucAccount.memberEdit.form.entName')"
          prop="entName"
          required
        >
          <el-input
            v-model="form.entName"
            :placeholder="$t('ucAccount.memberEdit.placeholder.entName')"
            class="form-input"
            clearable
            disabled
          />
        </el-form-item>

        <el-form-item
          :label="$t('ucAccount.memberEdit.form.nature')"
          prop="nature"
          required
        >
          <el-input
            v-model="form.nature"
            :placeholder="$t('ucAccount.memberEdit.placeholder.nature')"
            class="form-input"
            clearable
          />
        </el-form-item>

        <el-form-item
          :label="$t('ucAccount.memberEdit.form.industryType')"
          prop="industryType"
          required
        >
          <el-input
            v-model="form.industryType"
            :placeholder="$t('ucAccount.memberEdit.placeholder.industryType')"
            class="form-input"
            clearable
          />
        </el-form-item>

        <el-form-item
          :label="$t('ucAccount.memberEdit.form.mainProducts')"
          prop="mainProducts"
          required
        >
          <el-input
            v-model="form.mainProducts"
            :placeholder="$t('ucAccount.memberEdit.placeholder.mainProducts')"
            class="form-input"
            clearable
          />
        </el-form-item>

        <el-form-item
          :label="$t('ucAccount.memberEdit.form.introduce')"
          prop="introduce"
          required
          class="upload-form-item"
        >
          <el-input
            v-model="form.introduce"
            type="textarea"
            :rows="6"
            :placeholder="$t('ucAccount.memberEdit.placeholder.introduce')"
            class="form-textarea"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <el-form-item
          :label="$t('ucAccount.memberEdit.form.mainProductsList')"
          prop="mainProductsList"
          required
          class="tags-form-item"
        >
          <div class="main-products-block">
            <div class="main-products-row">
              <div class="product-input-inner-wrap">
                <el-input
                  v-model="mainProductInput"
                  :placeholder="$t('ucAccount.memberEdit.placeholder.mainProductItem')"
                  class="product-input-inner"
                  clearable
                  @input="handleMainProductInput"
                  @keyup.enter="addMainProduct"
                />
                <span
                  class="add-product-text"
                  @click="addMainProduct"
                >{{ $t('ucAccount.memberEdit.btn.add') }}</span>
              </div>
            </div>
            <div
              v-if="form.mainProductsList?.length"
              class="main-products-tags"
            >
              <span
                v-for="(item, index) in form.mainProductsList"
                :key="index"
                class="product-tag"
              >
                {{ item }}
                <el-icon
                  class="tag-close"
                  @click="removeMainProduct(index)"
                ><Close /></el-icon>
              </span>
            </div>
          </div>
        </el-form-item>

        <el-form-item class="submit-btn-item">
          <el-button
            type="primary"
            class="submit-btn"
            :loading="submitting"
            @click="handleSubmit"
          >
            {{ $t('ucAccount.memberEdit.btn.confirm') }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Close } from '@element-plus/icons-vue'
import { getMemberUnitInfoByEntId, updateMemberUnit } from '@/api/memberUnit'
import ImageUpload from '@/components/ImageUpload/index.vue'
import { showEnhancedConfirmDialog } from '@/utils/messageUtils'

export default {
  name: 'AssociationMemberInfoEdit',
  components: {
    ImageUpload,
    Close
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      form: {
        id: null,
        unitAvatar: '',
        entName: '',
        nature: '',
        industryType: '',
        mainProducts: '',
        introduce: '',
        mainProductsList: []
      },
      mainProductInput: '',
      mainProductCommaWarnAt: 0,
      submitting: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    rules() {
      return {
        unitAvatar: [{ required: true, message: this.$t('ucAccount.memberEdit.rule.uploadAvatar'), trigger: 'change' }],
        entName: [{ required: true, message: this.$t('ucAccount.memberEdit.rule.inputEntName'), trigger: 'blur' }],
        nature: [{ required: true, message: this.$t('ucAccount.memberEdit.rule.inputNature'), trigger: 'blur' }],
        industryType: [{ required: true, message: this.$t('ucAccount.memberEdit.rule.inputIndustry'), trigger: 'blur' }],
        mainProducts: [{ required: true, message: this.$t('ucAccount.memberEdit.rule.inputMainProducts'), trigger: 'blur' }],
        introduce: [{ required: true, message: this.$t('ucAccount.memberEdit.rule.inputIntroduce'), trigger: 'blur' }],
        mainProductsList: [
          {
            required: true,
            type: 'array',
            min: 1,
            message: this.$t('ucAccount.memberEdit.rule.minMainProducts'),
            trigger: 'change'
          }
        ]
      }
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.loadMemberUnitInfo()
      }
    }
  },
  methods: {
    handleMainProductInput(val) {
      const raw = val == null ? '' : String(val)
      if (!/[，,]/.test(raw)) return
      const next = raw.replace(/[，,]/g, '')
      this.mainProductInput = next
      const now = Date.now()
      if (now - this.mainProductCommaWarnAt > 1500) {
        this.mainProductCommaWarnAt = now
        ElMessage.warning(this.$t('ucAccount.memberEdit.msg.noComma'))
      }
    },
    addMainProduct() {
      const val = (this.mainProductInput || '').trim()
      if (!val) return
      if (/[，,]/.test(val)) {
        ElMessage.warning(this.$t('ucAccount.memberEdit.msg.noComma'))
        return
      }
      if (!this.form.mainProductsList) this.form.mainProductsList = []
      if (this.form.mainProductsList.includes(val)) {
        ElMessage.warning(this.$t('ucAccount.memberEdit.msg.duplicateProduct'))
        return
      }
      this.form.mainProductsList.push(val)
      this.mainProductInput = ''
      this.$nextTick(() => this.$refs.formRef?.validateField('mainProductsList', () => {}))
    },
    removeMainProduct(index) {
      this.form.mainProductsList.splice(index, 1)
      this.$nextTick(() => this.$refs.formRef?.validateField('mainProductsList', () => {}))
    },
    async loadMemberUnitInfo() {
      const mainBody = this.$auth.getMainBody()
      if (!mainBody?.mainBodyId) return
      try {
        const res = await getMemberUnitInfoByEntId(mainBody.mainBodyId)
        if (res && res.data) {
          const d = res.data
          this.form = {
            id: d.id,
            unitAvatar: d.unitAvatar || '',
            entName: d.entName || '',
            nature: d.nature || '',
            industryType: d.industryType || '',
            mainProducts: typeof d.mainProducts === 'string' ? d.mainProducts : (d.mainProducts || ''),
            introduce: d.introduce || '',
            mainProductsList: Array.isArray(d.mainProductsList) ? [...d.mainProductsList] : (typeof d.mainProductsList === 'string' ? d.mainProductsList.split(/[,，]/).map(s => s.trim()).filter(Boolean) : [])
          }
          this.mainProductInput = ''
        }
      } catch (e) {
        console.error('获取会员单位信息失败', e)
        ElMessage.error(this.$t('ucAccount.memberEdit.msg.loadFail'))
      }
    },
    handleClosed() {
      this.$refs.formRef?.resetFields()
      this.form = {
        id: null,
        unitAvatar: '',
        entName: '',
        nature: '',
        industryType: '',
        mainProducts: '',
        introduce: '',
        mainProductsList: []
      }
      this.mainProductInput = ''
    },
    async handleSubmit() {
      try {
        await this.$refs.formRef.validate()
      } catch (e) {
        return
      }

      const confirmed = await showEnhancedConfirmDialog(
        this.$t('ucAccount.memberEdit.confirm.message'),
        this.$t('ucAccount.memberEdit.confirm.title'),
        this.$t('ucAccount.memberEdit.confirm.ok'),
        this.$t('ucAccount.memberEdit.confirm.cancel'),
        { type: 'warning' }
      )
      if (!confirmed) return

      this.submitting = true
      try {
        const payload = {
          id: this.form.id,
          unitAvatar: this.form.unitAvatar,
          entName: this.form.entName,
          nature: this.form.nature,
          industryType: this.form.industryType,
          introduce: this.form.introduce,
          // 主营产品（单行文本）
          mainProducts: this.form.mainProducts || '',
          // 主要产品（标签列表，逗号分隔传给后端）
          mainProductsList: this.form.mainProductsList?.length ? this.form.mainProductsList.join(',') : ''
        }
        const res = await updateMemberUnit(payload)
        if (res && res.code === 200) {
          ElMessage.success(this.$t('ucAccount.memberEdit.msg.submitSuccess'))
          this.$emit('success')
          this.dialogVisible = false
        } else {
          ElMessage.error(res?.msg || this.$t('ucAccount.memberEdit.msg.submitFail'))
        }
      } catch (e) {
        ElMessage.error(e?.message || this.$t('ucAccount.memberEdit.msg.submitFail'))
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.association-member-info-edit-dialog {
  :deep(.el-dialog__header) {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #eee;
    .el-dialog__title {
      font-family: PingFang SC, PingFang SC;
      font-weight: 500;
      font-size: 14px;
      color: #333333;
      text-align: left;
      font-style: normal;
      text-transform: none;
    }
    .el-dialog__headerbtn {
      top: 20px;
    }
  }
  :deep(.el-dialog__body) {
    padding: 0 24px 24px;
  }
}

.identity-form-content {
  padding: 0;
  --form-content-width: 100%;


  .identity-form {
    width: 100%;

    :deep(.el-form-item) {
      margin-bottom: 24px;
      display: flex;
      align-items: center;
    }

    :deep(.el-form-item__label) {
      color: #333333;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.5;
      height: auto;
      padding-top: 0;
      padding-bottom: 0;
    }

    :deep(.el-form-item__content) {
      display: flex;
      align-items: center;
    }

    :deep(.upload-form-item) {
      align-items: flex-start;
      .el-form-item__label {
        align-items: flex-start;
      }
      .el-form-item__content {
        align-items: flex-start;
      }
    }

    :deep(.submit-btn-item) {
      .el-form-item__content {
        justify-content: center;
        margin-left: 0 !important;
      }
    }

    /* 主要产品：标签与输入框行垂直居中对齐（输入框 40px，标签单行约 21px） */
    :deep(.tags-form-item) {
      align-items: flex-start;
      .el-form-item__label {
        padding-top: 10px;
      }
      .el-form-item__content {
        align-items: flex-start;
      }
    }

    .form-input,
    :deep(.el-input) {
      width: var(--form-content-width);
      max-width: 100%;
      height: 40px;
    }

    :deep(.el-input__wrapper) {
      height: 40px;
      min-height: 40px;
      border-radius: 6px;
      border: 1px solid #e8e8e8 !important;
      box-shadow: none !important;
      box-sizing: border-box;
      background-color: #fff;
      padding: 0 15px;

      &:hover {
        border-color: #e8e8e8 !important;
      }
    }

    :deep(.el-textarea__inner) {
      border-radius: 6px;
      border: 1px solid #e8e8e8 !important;
      box-shadow: none !important;
      box-sizing: border-box;
      background-color: #fff;

      &:hover {
        border-color: #e8e8e8 !important;
      }
    }

    :deep(.el-input.is-focus .el-input__wrapper),
    :deep(.el-input.is-focused .el-input__wrapper),
    :deep(.el-textarea.is-focus .el-textarea__inner),
    :deep(.el-textarea.is-focused .el-textarea__inner) {
      border-color: #1890ff !important;
    }

    /* 与 PersonalIdentityVerification 一致的禁用样式 */
    :deep(.el-input.is-disabled .el-input__wrapper) {
      background-color: #f5f7fa !important;
      border-color: #e8e8e8 !important;
      cursor: not-allowed;
    }

    :deep(.el-input.is-disabled .el-input__inner) {
      color: #c0c4cc;
      -webkit-text-fill-color: #c0c4cc;
    }

    .form-textarea :deep(.el-textarea__inner) {
      padding: 8px 15px;
      font-size: 14px;
      color: #333333;
      min-height: 100px;
    }

    .submit-btn {      width: 165px;
      height: 46px;
      font-size: 16px;
      font-weight: 500;
      border: none;
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
      transition: all 0.3s;


      background: #0D62F3;
      border-radius: 6px 6px 6px 6px;
      border: 1px solid #0D62F3;

      &:hover {
        background: #0D62F3;
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
      }
    }
  }
}

.main-products-block {
  width: 100%;
  max-width: var(--form-content-width);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
}

.main-products-block .main-products-tags {
  margin-top: 12px;
}

.main-products-row {
  width: 100%;
  max-width: var(--form-content-width);
}

.product-input-inner-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-sizing: border-box;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover {
    border-color: #e8e8e8;
  }
  &:focus-within {
    border-color: #1890ff !important;
  }

  .product-input-inner {
    flex: 1;
    min-width: 0;
    height: 40px !important;
  }
  .product-input-inner :deep(.el-input__wrapper) {
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    height: 38px;
    min-height: 38px;
    padding: 0 0 0 15px;
    background: transparent !important;
  }
  .add-product-text {
    flex-shrink: 0;
    padding: 0 15px;
    font-size: 14px;
    color: #0D62F3;
    cursor: pointer;
    user-select: none;

    &:hover {
      opacity: 0.85;
    }
  }
}

.main-products-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.product-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 35px;
  padding: 0 10px;
  box-sizing: border-box;
  background: rgba(13, 98, 243, 0.1);
  border-radius: 19px;
  border: 1px solid #0D62F3;
  font-family: PingFang SC, PingFang SC;
  font-weight: 500;
  font-size: 14px;
  color: #0D62F3;
  text-align: center;
  font-style: normal;
  text-transform: none;
  .tag-close {
    font-size: 12px;
    cursor: pointer;
    margin-left: 2px;
    &:hover {
      color: #0d62f3;
      opacity: 0.8;
    }
  }
}

@media (max-width: 768px) {
  .association-member-info-edit-dialog {
    width: calc(100vw - 16px) !important;
    max-width: calc(100vw - 16px);
    margin: 8px auto !important;
    border-radius: 10px;

    :deep(.el-dialog__header) {
      padding: 14px 16px;

      .el-dialog__title {
        display: block;
        padding-right: 28px;
        font-size: 16px;
        line-height: 1.4;
        word-break: break-word;
      }

      .el-dialog__headerbtn {
        top: 12px;
        right: 12px;
      }
    }

    :deep(.el-dialog__body) {
      max-height: calc(100vh - 78px);
      padding: 12px;
      overflow-x: hidden;
      overflow-y: auto;
      box-sizing: border-box;
      -webkit-overflow-scrolling: touch;
    }
  }

  .identity-form-content {
    width: 100%;
    min-width: 0;
  }

  .identity-form-content .identity-form {
    width: 100%;
    min-width: 0;

    :deep(.el-form-item) {
      display: block;
      margin-bottom: 18px;
    }

    :deep(.el-form-item__label) {
      display: flex;
      width: 100% !important;
      justify-content: flex-start;
      margin-bottom: 8px;
      padding: 0;
      line-height: 1.5;
      text-align: left;
      white-space: normal;
      word-break: break-word;
    }

    :deep(.el-form-item__content) {
      display: block;
      width: 100%;
      max-width: 100%;
      margin-left: 0 !important;
      box-sizing: border-box;
    }

    :deep(.tags-form-item .el-form-item__label) {
      padding-top: 0;
    }

    .form-input,
    :deep(.el-input),
    :deep(.el-input__wrapper),
    :deep(.el-textarea),
    :deep(.el-textarea__inner) {
      width: 100% !important;
      max-width: 100%;
      box-sizing: border-box;
    }

    .form-textarea :deep(.el-textarea__inner) {
      min-height: 118px;
    }

    .upload-wrapper,
    :deep(.image-upload-wrapper),
    :deep(.image-uploader),
    :deep(.image-uploader .el-upload) {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      box-sizing: border-box;
    }

    :deep(.image-upload-placeholder),
    :deep(.image-preview) {
      width: 100% !important;
      max-width: 100%;
      height: auto !important;
      aspect-ratio: 16 / 9;
      box-sizing: border-box;
    }

    :deep(.upload-label) {
      width: 100%;
      text-align: left;
      line-height: 1.5;
      word-break: break-word;
    }

    .main-products-block,
    .main-products-row {
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }

    .product-input-inner-wrap {
      width: 100%;
      max-width: 100%;
    }

    .product-input-inner-wrap .add-product-text {
      padding: 0 12px;
    }

    .main-products-tags {
      gap: 8px;
    }

    .product-tag {
      max-width: 100%;
      height: auto;
      min-height: 32px;
      padding: 6px 10px;
      line-height: 1.4;
      text-align: left;
      white-space: normal;
      word-break: break-word;
    }

    :deep(.submit-btn-item) {
      margin-bottom: 0;
    }

    .submit-btn {
      width: 100%;
      max-width: 240px;
      height: 42px;
      font-size: 15px;
    }
  }
}

@media (max-width: 480px) {
  .association-member-info-edit-dialog {
    width: calc(100vw - 12px) !important;
    max-width: calc(100vw - 12px);

    :deep(.el-dialog__body) {
      padding: 10px;
    }
  }

  .identity-form-content .identity-form {
    .product-input-inner-wrap {
      height: auto;
      min-height: 40px;
    }

    .submit-btn {
      width: 100%;
      max-width: none;
    }
  }
}
</style>

<style lang="scss">
@media (max-width: 768px) {
  .association-member-info-edit-dialog.el-dialog,
  .association-member-info-edit-dialog .el-dialog {
    width: calc(100vw - 16px) !important;
    max-width: calc(100vw - 16px) !important;
    margin: 8px auto !important;
    box-sizing: border-box;
  }

  .association-member-info-edit-dialog.el-dialog .el-dialog__header,
  .association-member-info-edit-dialog .el-dialog__header {
    padding: 14px 16px;
    box-sizing: border-box;
  }

  .association-member-info-edit-dialog.el-dialog .el-dialog__body,
  .association-member-info-edit-dialog .el-dialog__body {
    width: 100%;
    max-width: 100%;
    max-height: calc(100vh - 78px);
    padding: 12px;
    overflow-x: hidden;
    overflow-y: auto;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }

  .association-member-info-edit-dialog .identity-form,
  .association-member-info-edit-dialog .identity-form-content {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .association-member-info-edit-dialog .identity-form .el-form-item {
    display: block;
    margin-bottom: 18px;
  }

  .association-member-info-edit-dialog .identity-form .el-form-item__label {
    display: flex;
    width: 100% !important;
    justify-content: flex-start;
    margin-bottom: 8px;
    padding: 0;
    text-align: left;
    white-space: normal;
    word-break: break-word;
  }

  .association-member-info-edit-dialog .identity-form .el-form-item__content {
    display: block;
    width: 100%;
    max-width: 100%;
    margin-left: 0 !important;
    box-sizing: border-box;
  }

  .association-member-info-edit-dialog .identity-form .el-input,
  .association-member-info-edit-dialog .identity-form .el-input__wrapper,
  .association-member-info-edit-dialog .identity-form .el-textarea,
  .association-member-info-edit-dialog .identity-form .el-textarea__inner,
  .association-member-info-edit-dialog .main-products-block,
  .association-member-info-edit-dialog .main-products-row,
  .association-member-info-edit-dialog .product-input-inner-wrap,
  .association-member-info-edit-dialog .upload-wrapper,
  .association-member-info-edit-dialog .image-upload-wrapper,
  .association-member-info-edit-dialog .image-uploader,
  .association-member-info-edit-dialog .image-uploader .el-upload {
    width: 100% !important;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .association-member-info-edit-dialog .image-upload-placeholder,
  .association-member-info-edit-dialog .image-preview {
    width: 100% !important;
    max-width: 100%;
    height: auto !important;
    aspect-ratio: 16 / 9;
    box-sizing: border-box;
  }

  .association-member-info-edit-dialog .upload-label {
    width: 100%;
    text-align: left;
    line-height: 1.5;
    word-break: break-word;
  }

  .association-member-info-edit-dialog .submit-btn {
    width: 100% !important;
    max-width: 240px;
    height: 42px;
  }
}

@media (max-width: 480px) {
  .association-member-info-edit-dialog.el-dialog,
  .association-member-info-edit-dialog .el-dialog {
    width: calc(100vw - 12px) !important;
    max-width: calc(100vw - 12px) !important;
  }

  .association-member-info-edit-dialog.el-dialog .el-dialog__body,
  .association-member-info-edit-dialog .el-dialog__body {
    padding: 10px;
  }

  .association-member-info-edit-dialog .submit-btn {
    max-width: none;
  }
}
</style>
